

































































import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onUnmounted,
} from '@nuxtjs/composition-api';
import { AnnularThrobber } from '@bambeehr/pollen';
import { getCookie } from '@/lib/cookie-helpers';
import CookieNames from '@/constants/Cookies';

const SCORM_AGAIN_CDN =
  process.env.SCORM_AGAIN_CDN ??
  'https://cdn.jsdelivr.net/npm/scorm-again@1.7.1/dist/scorm-again.js';
const SCORM_AGAIN_SRI =
  process.env.SCORM_AGAIN_SRI ??
  'sha384-IZOgoiUDFCdOtfmr0YnFFAc/xBqXX6utDAgQzVR1f6rsdAJB/AdkwfkAm3JMYi/T';
const SCRIPT_LOAD_TIMEOUT = 15000;

enum ScormEventType {
  SESSION_START = 'session_start',
  SESSION_END = 'session_end',
  COMMIT = 'commit',
  COMPLETED = 'completed',
}

enum CmiElement {
  LESSON_STATUS = 'cmi.core.lesson_status',
}

enum LessonStatus {
  COMPLETED = 'completed',
  PASSED = 'passed',
}

const RETRY_CONFIG: Record<string, number> = {
  [ScormEventType.SESSION_START]: 1,
  [ScormEventType.COMMIT]: 3,
  [ScormEventType.COMPLETED]: 5,
  [ScormEventType.SESSION_END]: 0,
};

// Instance counter for window.API ownership — prevents collision during route transitions
let scormInstanceCounter = 0;

class ApiError extends Error {
  constructor(public status: number) {
    super(`HTTP ${status}`);
  }
}

function loadScript(src: string, integrity?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    if (integrity) {
      script.integrity = integrity;
      script.crossOrigin = 'anonymous';
    }
    const timer = setTimeout(() => {
      reject(new Error(`Timeout loading ${src}`));
    }, SCRIPT_LOAD_TIMEOUT);
    script.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });
}

export default defineComponent({
  name: 'ScormPlayer',
  components: { AnnularThrobber },
  props: {
    ticketId: { type: String, required: true },
  },
  emits: ['completed', 'started', 'error'],
  setup(props, { emit }) {
    const loading = ref(true);
    const loadingMessage = ref('Initializing...');
    const error = ref<string | null>(null);
    const frameSrc = ref('about:blank');
    const completionState = ref<'playing' | 'saving' | 'completed' | 'failed'>(
      'playing'
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let scormAPI: any = null;
    let destroyed = false;
    let pendingCompletionData: Record<string, unknown> = {};
    const instanceId = ++scormInstanceCounter;

    const showIframe = computed(
      () =>
        frameSrc.value !== 'about:blank' &&
        !error.value &&
        (completionState.value === 'playing' ||
          completionState.value === 'saving')
    );

    const apiBase = process.env.PUBLICAPI_URL ?? '';

    function getToken(): string {
      return getCookie(CookieNames.ACCESS_TOKEN) ?? '';
    }

    async function apiFetch(
      method: string,
      path: string,
      body?: Record<string, unknown>
    ) {
      const opts: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
      };
      if (body) opts.body = JSON.stringify(body);
      const res = await fetch(`${apiBase}${path}`, opts);
      if (!res.ok) throw new ApiError(res.status);
      return res.json();
    }

    function sendEvent(
      eventType: string,
      data: Record<string, unknown>,
      metadata?: Record<string, unknown>
    ) {
      const payload: Record<string, unknown> = {
        eventType,
        timestamp: new Date().toISOString(),
        data,
      };
      if (metadata) payload.metadata = metadata;
      return apiFetch(
        'POST',
        `/training/v1/scorm/${props.ticketId}`,
        payload
      );
    }

    async function sendEventWithRetry(
      eventType: string,
      data: Record<string, unknown>,
      metadata?: Record<string, unknown>
    ): Promise<void> {
      const maxRetries = RETRY_CONFIG[eventType] ?? 0;
      let lastError: Error | undefined;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (destroyed) return;

        try {
          await sendEvent(eventType, data, metadata);
          return;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));

          // Don't retry client errors (4xx) — they won't resolve with retry
          if (lastError instanceof ApiError && lastError.status < 500) {
            throw lastError;
          }

          if (attempt < maxRetries) {
            await new Promise((r) =>
              setTimeout(r, 1000 * Math.pow(2, attempt))
            );
          }
        }
      }

      throw lastError!;
    }

    function getCmiData() {
      if (!scormAPI?.cmi) return {};
      const cmi = scormAPI.cmi;
      const rawScore = cmi.core?.score?.raw;
      const parsed =
        rawScore !== '' && rawScore !== undefined
          ? parseFloat(rawScore)
          : undefined;
      return {
        status: cmi.core?.lesson_status || undefined,
        location: cmi.core?.lesson_location || undefined,
        score:
          parsed !== undefined && !isNaN(parsed)
            ? { raw: parsed }
            : undefined,
        totalTime: cmi.core?.total_time || undefined,
        suspendData: cmi.suspend_data || undefined,
      };
    }

    function cleanupWindowApi() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).__scormOwnerId === instanceId) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (window as any).API;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (window as any).__scormOwnerId;
      }
    }

    async function attemptCompletion() {
      completionState.value = 'saving';
      try {
        await sendEventWithRetry(
          ScormEventType.COMPLETED,
          pendingCompletionData
        );
        if (destroyed) return;
        completionState.value = 'completed';
        frameSrc.value = 'about:blank';
        cleanupWindowApi();
        emit('completed');
      } catch (err) {
        if (destroyed) return;
        completionState.value = 'failed';
        console.error('Completion failed after retries:', err);
        emit('error', err);
      }
    }

    function handleCompletion() {
      if (completionState.value !== 'playing') return;
      pendingCompletionData = getCmiData();
      attemptCompletion();
    }

    function retryCompletion() {
      if (completionState.value !== 'failed') return;
      attemptCompletion();
    }

    async function initScorm() {
      loading.value = true;
      error.value = null;
      loadingMessage.value = 'Loading course data...';

      try {
        await loadScript(SCORM_AGAIN_CDN, SCORM_AGAIN_SRI);

        if (destroyed) return;

        const data = await apiFetch(
          'GET',
          `/training/v1/scorm/${props.ticketId}`
        );

        if (destroyed) return;

        if (!data.contentUrl) {
          error.value = 'No content URL available for this course.';
          loading.value = false;
          return;
        }

        // Already completed?
        if (data.progress?.completedAt) {
          completionState.value = 'completed';
          loading.value = false;
          emit('completed');
          return;
        }

        // Init scorm-again
        loadingMessage.value = 'Initializing player...';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        scormAPI = new (window as any).Scorm12API({
          autocommit: false, // We manually commit via LMSCommit to control when progress is saved
          logLevel: 1, // Errors only (0=none, 1=errors, 2=warn, 3=info, 4=debug)
          selfReportSessionTime: true, // Let scorm-again track session_time automatically
        });

        scormAPI.cmi.core.student_id = data.learner?.id ?? 'learner';
        scormAPI.cmi.core.student_name = data.learner?.name ?? 'Learner';

        // Restore progress
        if (data.progress) {
          if (data.progress.location)
            scormAPI.cmi.core.lesson_location = data.progress.location;
          if (data.progress.suspendData)
            scormAPI.cmi.suspend_data = data.progress.suspendData;
          if (data.progress.status)
            scormAPI.cmi.core.lesson_status = data.progress.status;
          if (data.progress.totalTime)
            scormAPI.cmi.core.total_time = data.progress.totalTime;
          scormAPI.cmi.core.entry = 'resume';
        } else {
          scormAPI.cmi.core.entry = 'ab-initio';
        }

        // Create window.API for SCORM 1.2 discovery
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__scormOwnerId = instanceId;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).API = {
          LMSInitialize(p: string) {
            const r = scormAPI.lmsInitialize(p);
            sendEventWithRetry(ScormEventType.SESSION_START, {}).catch(
              (e: Error) => console.warn('session_start failed:', e)
            );
            emit('started');
            return r;
          },
          LMSFinish(p: string) {
            const cmi = getCmiData();
            const r = scormAPI.lmsFinish(p);
            sendEvent(ScormEventType.SESSION_END, cmi).catch((e: Error) =>
              console.warn('session_end failed:', e)
            );
            return r;
          },
          LMSGetValue(e: string) {
            return scormAPI.lmsGetValue(e);
          },
          LMSSetValue(e: string, v: string) {
            const r = scormAPI.lmsSetValue(e, v);
            if (e === CmiElement.LESSON_STATUS) {
              if (
                v === LessonStatus.COMPLETED ||
                v === LessonStatus.PASSED
              ) {
                handleCompletion();
              }
            }
            return r;
          },
          LMSCommit(p: string) {
            const r = scormAPI.lmsCommit(p);
            sendEventWithRetry(ScormEventType.COMMIT, getCmiData()).catch(
              (e: Error) => console.warn('commit failed after retries:', e)
            );
            return r;
          },
          LMSGetLastError() {
            return scormAPI.lmsGetLastError();
          },
          LMSGetErrorString(c: string) {
            return scormAPI.lmsGetErrorString(c);
          },
          LMSGetDiagnostic(c: string) {
            return scormAPI.lmsGetDiagnostic(c);
          },
        };

        // Load dispatch in iframe via same-origin proxy (_redirects)
        loadingMessage.value = 'Loading content...';
        frameSrc.value = data.contentUrl;
      } catch (e: unknown) {
        if (destroyed) return;
        const msg = e instanceof Error ? e.message : String(e);
        console.error('SCORM init error:', e);
        error.value = `Failed to load course: ${msg}`;
        loading.value = false;
        emit('error', e);
      }
    }

    function onFrameLoad() {
      if (frameSrc.value !== 'about:blank') {
        loading.value = false;
      }
    }

    onMounted(initScorm);

    onUnmounted(() => {
      destroyed = true;
      frameSrc.value = 'about:blank';
      cleanupWindowApi();
      scormAPI = null;
    });

    return {
      loading,
      loadingMessage,
      error,
      frameSrc,
      completionState,
      showIframe,
      onFrameLoad,
      initScorm,
      retryCompletion,
    };
  },
});
